import React, { useState, useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

interface Rechnung {
    id: number;
    pdfPath?: string | null;
}

interface PdfStatusCardProps {
    rechnung: Rechnung;
    onPdfCreated: (pdfPath: string) => void;
}

const PdfStatusCard: React.FC<PdfStatusCardProps> = ({ rechnung, onPdfCreated }) => {
    const authContext = useContext(AuthContext);
    const [creatingPdf, setCreatingPdf] = useState(false);

    if (!authContext) {
        throw new Error('PdfStatusCard must be used within an AuthProvider');
    }

    const { token } = authContext;

    const handleCreatePdf = async () => {
        setCreatingPdf(true);
        try {
            const response = await fetch(`/api/rechnungen/${rechnung.id}/createPdf`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Erstellen der PDF');
            }

            const data = await response.json();
            onPdfCreated(data.pdfPath);
            toast.success('PDF erfolgreich erstellt');
        } catch (error) {
            console.error('Error creating PDF:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Erstellen der PDF');
        } finally {
            setCreatingPdf(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            const response = await fetch(`/api/rechnungen/${rechnung.id}/downloadPdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Download fehlgeschlagen');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Rechnung_${rechnung.id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('PDF heruntergeladen');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Herunterladen der PDF');
        }
    };

    return (
        <div className="pdf-status-card">
            {rechnung.pdfPath ? (
                <div className="pdf-status-content">
                    <span className="pdf-status-label">Rechnung PDF:</span>
                    <span className="pdf-filename">{rechnung.pdfPath.split('/').pop()}</span>
                    <button 
                        className="download-pdf-btn" 
                        onClick={handleDownloadPdf}
                        title="PDF herunterladen"
                    >
                        ⬇ Download PDF
                    </button>
                </div>
            ) : (
                <div className="pdf-status-content">
                    <span className="pdf-status-label">Keine PDF erstellt</span>
                    <button 
                        className="create-pdf-btn" 
                        onClick={handleCreatePdf}
                        disabled={creatingPdf}
                        title="PDF erstellen"
                    >
                        {creatingPdf ? '⏳ Erstelle...' : '📄 PDF erstellen'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default PdfStatusCard;
